import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from "@angular/core";
import { SpsTaskStatus, SpsTaskStatusIcons, TASK_QUEUE_NOTIFICATION_DURATION_MS, TooltipKind } from "@spscommerce/ds-shared";
import { Position, PositioningService } from "@spscommerce/positioning";

import { NgSpsTask } from "../../types/task-queue";
import { SpsTooltipComponent } from "../tooltip/sps-tooltip.component";

@Component({
    selector: "sps-task-queue",
    templateUrl: "./sps-task-queue.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpsTaskQueueComponent implements OnChanges {
    static readonly displayName = "sps-task-queue";
    static readonly props = {
        tasks: "Array<NgSpsTask>",
        notificationText: "string",
        notificationTooltipKind: "TooltipKind",
        tasksChange: { event: true, type: "Array<NgSpsTask>" },
        opened: { event: true, type: "void" },
        completedCleared: { event: true, type: "Array<NgSpsTask>" },
    };
    private _positioningService = new PositioningService();

    @Input() tasks: NgSpsTask[] = [];
    @Input() notificationText: string;
    @Input() notificationTooltipKind: TooltipKind = TooltipKind.DEFAULT;
    @Output() tasksChange = new EventEmitter<NgSpsTask[]>();
    @Output() opened = new EventEmitter<void>();

    @Output() completedCleared = new EventEmitter<NgSpsTask[]>();

    @HostBinding("class.sps-task-queue") readonly _spsTaskQueueClass = true;

    private _isOpen = false;
    @HostBinding("class.sps-task-queue--open")
    @HostBinding("class.z-stratum-dropdown")
    get isOpen() {
        return this._isOpen;
    }

    @HostBinding("class.sps-task-queue--no-tasks")
    get isEmpty() {
        return this.tasks && this.tasks.length === 0;
    }

    @ViewChild("button", { static: false }) button: ElementRef;
    @ViewChild("taskList", { static: false }) taskList: ElementRef;
    @ViewChild("tooltip", { static: false }) tooltip: SpsTooltipComponent;

    get hasTasks() {
        return this.tasks && this.tasks.length;
    }

    get unreadTaskCount() {
        return this.tasks ? this.tasks.filter(t => t.unread).length : 0;
    }

    get aTaskIsInProgress() {
        return this.tasks && this.tasks.some(t => t.status === SpsTaskStatus.IN_PROGRESS);
    }

    get aTaskIsCompleted() {
        return this.tasks && this.tasks.some(t => t.status !== SpsTaskStatus.IN_PROGRESS);
    }

    notificationTimeout: number;

    ngOnChanges(changes: SimpleChanges) {
        if (changes.notificationText && this.tooltip && this.hasTasks) {
            window.setTimeout(() => {
                this.tooltip.show();
                if (this.notificationTimeout) {
                    window.clearTimeout(this.notificationTimeout);
                }
                this.notificationTimeout = window.setTimeout(() => this.tooltip.hide(), TASK_QUEUE_NOTIFICATION_DURATION_MS);
            }, 0);
        }
    }

    open() {
        if (!this._isOpen && this.hasTasks) {
            this.opened.emit();
            this.tasksChange.emit(this.tasks);
            this._isOpen = true;
            this._positioningService.position(this.taskList.nativeElement, {
                position: Position.BOTTOM_RIGHT,
                relativeTo: this.button.nativeElement,
            });
        }
    }

    close() {
        if (this._isOpen) {
            this._isOpen = false;
            this._positioningService.release(this.taskList.nativeElement);
            this.tasks = this.tasks.map(t => Object.assign(t, { unread: false }));
        }
    }

    toggle() {
        if (this._isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    clearCompleted() {
        const inProgress = [];
        const complete = [];
        for (const task of this.tasks || []) {
            if (task.status === SpsTaskStatus.IN_PROGRESS) {
                inProgress.push(task);
            } else {
                complete.push(task);
            }
        }
        this.tasks = inProgress;
        this.tasksChange.emit(this.tasks);
        this.completedCleared.emit(complete);
        if (this.tasks.length === 0) {
            this.close();
        }
    }

    taskIcon(task: NgSpsTask) {
        return task.status === SpsTaskStatus.IN_PROGRESS
            ? SpsTaskStatusIcons[task.status]
            : `sps-icon sps-icon-${SpsTaskStatusIcons[task.status]}`;
    }

    subheadingIsTemplate(task: NgSpsTask) {
        return task.subheading && task.subheading instanceof TemplateRef;
    }

    @HostListener("click", ["$event"])
    onHostClick(event: MouseEvent) {
        event.stopPropagation();
    }

    @HostListener("document:click")
    onDocumentClick() {
        this.close();
    }
}
